import { FEATURE_FLAGS } from '@aibees/elephant/constants';
import { FeatureFlags } from '@aibees/elephant/types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { PeopleMetadataItem } from '@aibees/elephant/hooks/people/types';
import { SearchListType } from '@aibees/elephant/hooks/search-list/types';
import { IconType } from '@aibees/shared/components';
import { isFeatureEnabled } from '@aibees/shared/feature-flags';
import {
  getBulkSearchFilterFieldTags,
  peopleSearchLabelParser,
} from '../components/filter-tags/utils';
import { peopleLinkedinUrlRegexPatterns } from '../constants/config';
import { FilterCategory, SearchFilterBase, SearchFilterType } from '../types';
import { peopleSearchApiResponseMapper } from '../utils';
import { bulkUrlValidation } from '../utils/bulk-url-validtion';

export default function peopleSearchFilterConfig(): SearchFilterBase {
  return {
    id: 'People Search',
    name: 'People Search',
    icon: 'group-search',
    index: 18,
    layer: 3,
    type: SearchFilterType.ROOT,
    category: [FilterCategory.CONTACT_DATA],
    pinned: true,
    items: [
      {
        name: 'Manual / Bulk Search',
        type: SearchFilterType.NONE,
        icon: undefined,
        index: 0,
        key: '',
        field: '',
        utils: {
          tags: {
            config: {
              showAsCard: true,
              tagsCardTitle: 'Manual Search',
            },
          },
        },
        items: [
          {
            name: 'Include',
            type: SearchFilterType.CUSTOM_SELECT,
            index: 1,
            layer: 4,
            field: 'include_person_id',
            key: 'include_person_id',
            metadata: {
              color: 'primary',
              icon: 'add-circle-outline',
              preset: 'person_name',
              placeholder: 'Enter Full Name or LinkedIn URL',
              autocomplete: {
                recent: false,
                checkboxPosition: 'right',
                multiple: true,
                avartarFallbackIcon: 'person-outline',
              },
              delimiter: '::',
              limit: {
                max: {
                  size: 100,
                  message:
                    'Manual people search entry are limited to 100 records, please use the bulk Include/exclude to add more.',
                },
              },
            },
            searchKey: 'search',
            config: { labelKey: 'label', valueKey: 'value' },
            utils: {
              optionsApiFormatter: (
                data?: PeopleMetadataItem[],
                icon?: IconType,
                delimiter?: string
              ) => peopleSearchApiResponseMapper(data, icon, delimiter),
              tags: { labelFormatter: peopleSearchLabelParser },
            },
            options: [
              {
                type: 'URL',
                value: `/elephant-management/um/workspaces/:wid/persons/metadata-management?return=list&size=10&page=0`,
                label: '',
                icon: null,
                index: undefined,
              },
            ],
          },
          {
            name: 'Exclude',
            type: SearchFilterType.CUSTOM_SELECT,
            index: 2,
            layer: 4,
            key: 'exclude_person_id',
            field: 'exclude_person_id',
            metadata: {
              color: 'error',
              icon: 'block',
              preset: 'person_name',
              placeholder: 'Enter Full Name or LinkedIn URL',
              autocomplete: {
                recent: false,
                checkboxPosition: 'right',
                multiple: true,
                avartarFallbackIcon: 'person-outline',
              },
              delimiter: '::',
              limit: {
                max: {
                  size: 100,
                  message:
                    'Manual people search entry are limited to 100 records, please use the bulk Include/exclude to add more.',
                },
              },
            },
            searchKey: 'search',
            config: { labelKey: 'label', valueKey: 'value' },
            utils: {
              optionsApiFormatter: (
                data?: PeopleMetadataItem[],
                icon?: IconType,
                delimiter?: string
              ) => peopleSearchApiResponseMapper(data, icon, delimiter),
              tags: { labelFormatter: peopleSearchLabelParser },
            },
            options: [
              {
                type: 'URL',
                value: `/elephant-management/um/workspaces/:wid/persons/metadata-management?return=list&size=10&page=0`,
                label: '',
                icon: null,
                index: undefined,
              },
            ],
          },
        ],
      },
      {
        name: 'Bulk Manual Search',
        type: SearchFilterType.NONE,
        icon: undefined,
        index: 0,
        key: '',
        field: '',
        items: [
          {
            name: 'Bulk Include / Exclude',
            type: SearchFilterType.BULK,
            index: 2,
            layer: 3,
            key: '',
            field: '',
            icon: 'plus',
            comingSoon: !isFeatureEnabled(
              FEATURE_FLAGS,
              FeatureFlags.FILTER_PEOPLE_BULK_SEARCH
            ),
            items: [
              {
                field: 'bulk_person_linkedin_url',
                key: 'bulk_person_linkedin_url',
                index: 0,
                layer: 4,
                name: 'People Social Link',
                type: SearchFilterType.BULK,
                comingSoon: !isFeatureEnabled(
                  FEATURE_FLAGS,
                  FeatureFlags.FILTER_PEOPLE_BULK_SEARCH
                ),
                metadata: {
                  color: 'primary',
                  icon: 'block',
                  alternate: {
                    icon: 'eye-outlined',
                    name: 'View All',
                  },
                  validation: {
                    patternValidation: (inputs) =>
                      bulkUrlValidation(inputs, peopleLinkedinUrlRegexPatterns),
                  },
                  bulkInput: {
                    listType: SearchListType.PERSON_LINKEDIN,
                  },
                  includedExcludedLabel: 'People Included/Excluded',
                  dialog: {
                    subTitleItemLabel: 'URLs',
                    title: 'Bulk Include / Exclude People Search',
                  },
                },
                utils: {
                  tags: {
                    config: {
                      showAsCard: true,
                      tagsCardTitle: 'People Social Links Bulk',
                    },
                  },
                },
                items: [
                  {
                    field: 'bulk_include_person_linkedin_url',
                    key: 'bulk_include_person_linkedin_url',
                    index: 0,
                    layer: 4,
                    metadata: {
                      color: 'primary',
                      limit: {
                        max: {
                          size: 10000,
                          message:
                            'You can only add up to {0} included, remove at least {1} people to continue.',
                        },
                      },
                      dialog: {
                        subTitleItemLabel: 'URLs',
                        textArea: {
                          placeholder: `https://www.linkedin.com/in/nathanlatka/\nhttp://www.linkedin.com/in/nathanlatka/\nwww.linkedin.com/in/nathanlatka/\nlinkedin.com/in/nathanlatka/`,
                        },
                      },
                    },
                    name: 'Include People',
                    type: SearchFilterType.BULK,
                    utils: {
                      tags: {
                        getFilterItemTagFunc: getBulkSearchFilterFieldTags,
                      },
                    },
                  },
                  {
                    field: 'bulk_exclude_person_linkedin_url',
                    key: 'bulk_exclude_person_linkedin_url',
                    index: 0,
                    layer: 4,
                    metadata: {
                      color: 'error',
                      limit: {
                        max: {
                          size: 10000,
                          message:
                            'You can only add up to {0} excluded, remove at least {1} people to continue.',
                        },
                      },
                      dialog: {
                        subTitleItemLabel: 'URLs',
                        textArea: {
                          placeholder: `https://www.linkedin.com/in/nathanlatka/\nhttp://www.linkedin.com/in/nathanlatka/\nwww.linkedin.com/in/nathanlatka/\nlinkedin.com/in/nathanlatka/`,
                        },
                      },
                    },
                    name: 'Exclude People',
                    type: SearchFilterType.BULK,
                    utils: {
                      tags: {
                        getFilterItemTagFunc: getBulkSearchFilterFieldTags,
                      },
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
}
